import React, { useCallback, useMemo } from 'react'
import classNames from 'classnames'
import CarouselNav from '@/atoms/CarouselNav'
import { ClockIcon } from '@/atoms/Icons/ClockIcon'
import { GridIcon } from '@/atoms/Icons/GridIcon'
import { NoBooksIcon } from '@/atoms/Icons/NoBooksIcon'
import { NoCreditsIcon } from '@/atoms/Icons/NoCreditsIcon'
import { NoPhoneIcon } from '@/atoms/Icons/NoPhoneIcon'
import { NoPitchesIcon } from '@/atoms/Icons/NoPitchesIcon'
import { NoSubtitlesIcon } from '@/atoms/Icons/NoSubtitlesIcon'
import { NoVideoIcon } from '@/atoms/Icons/NoVideoIcon'
import { Image } from '@/atoms/Image'
import { PlayButtonThumbnailOverlay } from '@/atoms/PlayButtonThumbnailOverlay'
import { CaptionLG, EyebrowLG, HeadingMD, TitleLG, TitleMD } from '@/atoms/Text'
import { getCloudinaryImageUrl } from '@/utils/Cloudinary'
import { useDimensions } from '@/utils/ScreenUtils'
import { useSafeAnalytics } from '@/utils/analytics'
import { useTranslate } from '@/utils/translate/translate-client'
import useCarousel from '@/utils/useCarousel'
import useWindowSize from '@/utils/useWindowSize'
import { TorchSubmitButton } from '../ TorchSubmitButton'
import { AngelTorchVideoModal } from '../AngelTorchVideoModal'
import { SubmissionTip } from '../SubmissionTip'

export const TorchWatchSection: React.FC = () => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const [torchScrollerRef, { canScrollBack, canScrollForward, scrollBack, scrollForward }] =
    useCarousel<HTMLDivElement>()
  const { track } = useSafeAnalytics()
  const { width: containerWidth } = useDimensions(containerRef)
  const { innerWidth } = useWindowSize()
  const { t } = useTranslate('create')

  const iconSize = useMemo(() => {
    return innerWidth > 1024 ? 24 : 16
  }, [innerWidth])

  const leftPadding = innerWidth > 1680 && containerWidth && containerWidth > 1440 && (containerWidth - 1440) / 2

  const submissionTips = [
    {
      icon: <ClockIcon size={iconSize} color="core-gray-950" />,
      title: t('twoMinutesAnimation', '2+ minutes for animations'),
    },
    {
      icon: <ClockIcon size={iconSize} color="core-gray-950" />,
      title: t('fiveMinutesLiveAction', '5+ minutes for live action'),
    },
    { icon: <GridIcon size={iconSize} color="core-gray-950" />, title: t('continuousScenes', 'Continuous Scenes') },
    {
      icon: <NoVideoIcon size={iconSize} color1="core-gray-950" color2="core-gray-300" />,
      title: t('noTrailers', 'No Trailers or Reels'),
    },
    { icon: <NoSubtitlesIcon size={iconSize} color="core-gray-950" />, title: t('noExplicit', 'No Explicit Content') },
    {
      icon: <NoPitchesIcon size={iconSize} color1="core-gray-950" color2="core-gray-300" />,
      title: t('noPitches', 'No Filmmaker Pitches'),
    },
    {
      icon: <NoBooksIcon size={iconSize} color1="core-gray-950" color2="core-gray-300" />,
      title: t('noBooks', 'No Books or Scripts'),
    },
    {
      icon: <NoCreditsIcon size={iconSize} color1="core-gray-950" color2="core-gray-300" />,
      title: t('noCreditsOrLogos', 'No Credits or Logos'),
    },
    {
      icon: <NoPhoneIcon size={iconSize} color1="core-gray-950" color2="core-gray-300" />,
      title: t('noWebsitesOrQrCodes', 'No Websites or QR Codes'),
    },
  ]

  const torchCards = [
    {
      title: 'Cabrini',
      description: 'Completed Movie Torch',
      backgroundImageUrl: 'v1702511354/studio-app/catalog/0bb2bf06-5bfb-41f3-9cfc-fda092931bb9',
      torchUrl: 'https://media.angelstudios.com/copied-content/masters/a670cc4d-d3f4-4785-ac16-a7be92f68af6.m3u8',
    },
    {
      title: 'Fellowship',
      description: 'Unfinished Movie Torch',
      backgroundImageUrl:
        'https://images.angelstudios.com/image/upload/v1717438313/angel-studios/create/Fellowship.png',
      torchUrl: 'https://stream.mux.com/B02026eXeMCBCK94dRTTAca7S01Hpjx1llaOZjoTDZcBuA.m3u8',
    },
    {
      title: 'A Week Away',
      description: 'Series Torch',
      backgroundImageUrl: '/v1717426729/angel-studios/create/Screenshot_2024-06-03_at_8.58.36_AM.png',
      torchUrl: 'https://stream.mux.com/btmOwBBvS3bscMw02bwdFk5LVWZVtS01MYKwIARJCRV5A.m3u8',
    },
    {
      title: 'DAVID',
      description: 'Kids Torch',
      backgroundImageUrl: 'v1652468593/angel-app/for-your-consideration/David_16x9_KeyArt_en',
      torchUrl: 'https://media.angelstudios.com/copied-from-old-account/FYC/David/torch/david_torch_fyc.m3u8',
    },
    {
      title: 'Young DAVID',
      description: 'Animatic Kids Torch',
      backgroundImageUrl: 'v1704866747/angel-app/guild/torches/YD_Ep_4',
      torchUrl: 'https://stream.mux.com/PhmE02HAACBAPY73KZDniBy5pYtz8RNJUsgAGrI3BPyU.m3u8',
    },
  ]

  const handleSubmitClicked = useCallback(() => {
    track('Guild Submit Project Clicked')
  }, [track])

  return (
    <section
      className="bg-gray-900 bg-cover bg-center bg-no-repeat py-14 text-core-gray-950 md:py-[120px] se:py-4"
      ref={containerRef}
      style={{
        backgroundImage:
          'linear-gradient(180deg, #DFDEDD 0%, #9D9C9B 100%), url(https://images.angelstudios.com/image/upload/v1717178054/angel-studios/create/23d15d7e3a4d9fdc60ac8a81bb38a630_1.png)',
      }}
    >
      <div className="flex max-w-[1440px] flex-col px-4 md:px-12 lg:gap-20 lg:px-[120px] xl:flex-row xl:items-center xl:justify-between xl:gap-28 3lg:mx-auto 3lg:px-0">
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between md:gap-0 xl:flex-col xl:items-start xl:gap-12 se:gap-3">
          <HeadingMD
            weight="bold"
            color="core-gray-950"
            className="se:photon-title-md md:photon-heading-xl xl:photon-display md:max-w-[393px] xl:min-w-[484px] xl:max-w-[484px] xl:text-6xl"
          >
            {t('watchPopularTorches', 'Watch popular torches that have passed the Guild')}
          </HeadingMD>
          <div>
            <TorchSubmitButton isDark={false} handleSubmitClicked={handleSubmitClicked} />
          </div>
        </div>
        <div className="mt-10 flex max-w-[740px] flex-col gap-4 md:mt-16 md:gap-6 lg:mt-0 xl:max-w-[820px] 2xl:max-w-[960px] 3xl:max-w-[1200px]">
          <EyebrowLG weight="semibold" className="se:photon-eyebrow-md uppercase">
            {t('whatQualifiesAsATorch', 'What qualifies as a torch?')}
          </EyebrowLG>
          <div className="flex flex-row flex-wrap gap-3 md:gap-4 se:gap-x-1">
            {submissionTips.map((tip) => {
              return <SubmissionTip key={tip.title} {...tip} />
            })}
          </div>
        </div>
      </div>
      <div className="relative w-full">
        <div className="mt-16 max-w-[1440px] px-4 md:mt-32 md:px-12 lg:px-[120px] se:mt-8 3lg:mx-auto 3lg:px-0">
          <TitleLG weight="bold" className="se:photon-caption-lg md:photon-heading-sm lg:photon-heading-md">
            {t('torchExamples', 'Torch Examples')}
          </TitleLG>
        </div>
        <div
          className="relative flex w-full snap-x snap-mandatory flex-row gap-8 overflow-scroll px-4 scrollbar-none sm:flex-row md:px-12 lg:px-[120px] 3lg:px-0"
          ref={torchScrollerRef}
          style={{ paddingLeft: leftPadding ?? 0 }}
        >
          {torchCards.map((data) => {
            return <TorchCard key={data.title} {...data} />
          })}
        </div>
        <CarouselNav
          onClick={scrollBack}
          active={canScrollBack}
          orientation="horizontal"
          direction="back"
          className={classNames('ml-4', { 'opacity-75 hover:opacity-100': canScrollBack })}
          theme="dark"
        />
        <CarouselNav
          onClick={scrollForward}
          active={canScrollForward}
          orientation="horizontal"
          direction="forward"
          className={classNames('mr-4', { 'opacity-75 hover:opacity-100': canScrollForward })}
          theme="dark"
        />
      </div>
    </section>
  )
}

interface TorchCardProps {
  title?: string
  description?: string
  backgroundImageUrl?: string
  torchUrl: string
}

const TorchCard: React.FC<TorchCardProps> = ({ title, description, backgroundImageUrl, torchUrl }) => {
  const { track } = useSafeAnalytics()
  const [showModal, setShowModal] = React.useState(false)

  const handleModalClick = useCallback(() => {
    setShowModal(true)
    track('Watch For Free Card Clicked')
  }, [track])

  const handleClose = () => {
    setShowModal(false)
  }

  return (
    <div
      onClick={handleModalClick}
      className="flex cursor-pointer snap-center flex-col justify-between py-6 text-core-gray-950 lg:py-8 se:py-3"
    >
      <div className="relative overflow-hidden rounded-2xl">
        <Image
          className="flex max-h-[284px] min-h-[180px] min-w-[320px] lg:min-h-[284px] lg:min-w-[569px] se:min-h-[145px] se:min-w-[240px]"
          width={240}
          height={135}
          src={getCloudinaryImageUrl({ path: backgroundImageUrl ?? '' })}
          alt={title}
        />
        <PlayButtonThumbnailOverlay />
      </div>
      <div className="mt-[17px] flex flex-col items-start gap-1 text-left">
        <TitleMD className="md:photon-title-lg" weight="bold">
          {title}
        </TitleMD>
        <CaptionLG className="md:photon-paragraph-lg" color="core-gray-800" weight="medium">
          {description}
        </CaptionLG>
      </div>
      <AngelTorchVideoModal showModal={showModal} torchVideoUrl={torchUrl} handleClose={handleClose} />
    </div>
  )
}
